import request from '../utils/request'
import { invalidateFeedSnapshots } from '../utils/searchFeedReturn';
import { loadWithRecovery } from '../utils/loadWithRecovery';
import { appLocalStorage } from '../utils/storage';
import config from '../utils/config';
import { isSessionResponse } from '../utils/sessionResponse';
import { observeViewerOpinion, opinionRequestContext } from '../utils/viewerOpinionState';
import { reconcileDeletedSession } from '../accountDeletion/sessionProof';

import api from './api'

// эндпоинты, вызывающие AI-обработку на бэкенде (storyProcessing/reactionProcessing) — им нужен увеличенный таймаут
const AI_ENDPOINTS = ['storyAdd', 'reactionSaveVoting']
// Legacy GET routes can be writes; POST routes can be reads. Opt in explicitly.
const RECOVERABLE_READS = new Set([
  'unsubscribeLanding',
  'userByToken', 'userPreferences', 'userById', 'userByUsername', 'userFollowers',
  'storyList', 'storyStatistic', 'storyUsers', 'storyComment', 'storyLoad', 'storyLatestOpinion',
  'reactionLoad', 'reactionQuotes', 'reactionsLoad', 'reactionsLoadRefresh',
  'getLng', 'getCountry', 'getPage', 'linkPreview',
  'accountProtection',
]);
// Section/search return snapshots are read-only. Accepted writes invalidate
// every origin entry, including entries left in browser history by the editor.
const FEED_MUTATIONS = new Set([
  'reactionSaveVoting', 'reactionPrivacy', 'reactionDeleteById', 'reactionHide', 'reactionClear', 'reactionLike',
  'userFollowsAdd', 'userFollowsDelete', 'userUpdateFields', 'userPreferencesSave',
  'userLogin', 'userLogout', 'userLogoutAll', 'userDelete', 'storyAdd',
]);

const gen = (params, key) => {
  const apiPrefix = process.env.REACT_APP_API_URL;
  let url = apiPrefix + params
  let method = 'GET'

  const paramsArray = params.split(' ')
  if (paramsArray.length === 2) {
    method = paramsArray[0]
    url = apiPrefix + paramsArray[1]
  }

  return function(data, { signal } = {}) {
    const token = appLocalStorage.getItem(config.token);
    const opinionContext = opinionRequestContext();
    const read = (options = {}) => {
      if (RECOVERABLE_READS.has(key) && token !== appLocalStorage.getItem(config.token)) {
        return Promise.resolve({ success: false, cancelled: true });
      }
      return request({
        url,
        data,
        method,
        ...(signal ? { signal } : {}),
        // Browsers don't send GET bodies. Only this opt-in endpoint adds its
        // new filter to the query string; other legacy requests stay intact.
        ...(key === 'storyStatistic' && data?.area !== undefined ? { params: { area: data.area } } : {}),
        ...(key === 'reactionQuotes' ? { params: { offset: data?.offset ?? 0, limit: data?.limit ?? 20 } } : {}),
        ...(key === 'unsubscribeLanding' ? { params: {
          ...(data?.language ? { language: data.language } : {}),
          ...(data?.story != null ? { story: data.story } : {}),
        } } : {}),
        // Slider-only writes do not invoke AI moderation. Do not let a hung
        // vote hold the editor's metadata behind the long publication timeout.
        timeout: key === 'reactionSaveVoting' && data?.txt_message === undefined ? 10000
          : AI_ENDPOINTS.includes(key) ? 60000 : undefined,
        ...options,
      }).then(async response => {
        if (token && ((key === 'userByToken' && response?.success === true && response.user === null)
          || response?.statusCode === 401 || response?.errorCode === 'ACCOUNT_DELETED'
          || response?.message === 'ACCOUNT_DELETED')) {
          await reconcileDeletedSession(token);
        }
        if (response?.success && key === 'userByToken' && !isSessionResponse(response)) {
          return { success: false, statusCode: 502, errorCode: 'INVALID_SESSION_RESPONSE' };
        }
        if (response?.success && (key === 'reactionSaveVoting' || key === 'storyAdd')
          && (!Number.isSafeInteger(response.id) || response.id <= 0)) {
          return { success: false, statusCode: 502, errorCode: 'INVALID_PUBLICATION_RESPONSE' };
        }
        return response;
      });
    };
    const result = RECOVERABLE_READS.has(key) ? loadWithRecovery(read, { signal }) : read();
    return result.then(response => {
      // Observe only the recovery controller's accepted result. A suspended
      // transport may complete after its aborted attempt has been replaced.
      observeViewerOpinion(key, data, response, opinionContext);
      if (FEED_MUTATIONS.has(key) && response?.success) invalidateFeedSnapshots();
      return response;
    });
  }
}

const APIFunction = {}
for (const key in api) {
  APIFunction[key] = gen(api[key], key)
}

export default APIFunction;
